/**
 * texas542Display — pure classifier for the Texas §542 API response.
 *
 * Does NO arithmetic. It only decides which of four display states a backend payload is in, so
 * every §542 surface (card, read-back, SettlementPage, CaseDocumentCenter) agrees. A dollar
 * figure exists ONLY when `statutory_interest_claim.status === 'computed'` and `total_accrued`
 * is a finite number. The legacy `accrued_interest` field is never read: the API zeroes it when
 * nothing is computed, and that zero is what used to render as "$0.00".
 */

export type S542AckStatus = 'TIMELY' | 'BREACHED' | 'PRESUMED_BREACHED' | 'UNKNOWN';

export interface S542Candidate {
  trigger?: string | null;
  statute?: string | null;
  deadline?: string | null;
  start_date?: string | null;
  start_basis?: string | null;
  accrues_until?: string | null;
  days?: number | null;
  /** Present on recorded candidates in the election case — deliberately never rendered. */
  accrued?: number | null;
}

export interface StatutoryInterestClaim {
  status?: 'computed' | 'not_computed' | string | null;
  total_accrued?: number | null;
  daily_rate?: number | null;
  rate_pct?: number | null;
  start_date?: string | null;
  days_accruing?: number | null;
  start_basis?: string | null;
  trigger?: string | null;
  needs?: string[] | null;
  candidates?: S542Candidate[] | null;
  not_modelled?: string[] | null;
  note?: string | null;
  base?: number | null;
  base_label?: string | null;
  as_of?: string | null;
}

export interface S542Response {
  statutory_interest_claim?: StatutoryInterestClaim | null;
  interest_start_basis?: string | null;
  needs?: string[] | null;
  candidates?: S542Candidate[] | null;
  [key: string]: unknown;
}

export type S542Figure =
  | {
      kind: 'computed';
      total: number;
      startDate: string | null;
      startBasis: string | null;
      trigger: string | null;
      daysAccruing: number | null;
      ratePct: number | null;
      base: number | null;
      baseLabel: string | null;
      asOf: string | null;
    }
  | { kind: 'election'; candidates: S542Candidate[]; needs: string[] }
  | { kind: 'not_computed'; needs: string[]; note: string | null }
  | { kind: 'unavailable'; detail: string | null };

export function classifyS542(resp: S542Response | null | undefined): S542Figure {
  const sic = resp?.statutory_interest_claim ?? undefined;
  if (sic?.status === 'computed' && typeof sic.total_accrued === 'number' && Number.isFinite(sic.total_accrued)) {
    return {
      kind: 'computed',
      total: sic.total_accrued,
      startDate: sic.start_date ?? null,
      startBasis: sic.start_basis ?? null,
      trigger: sic.trigger ?? null,
      daysAccruing: sic.days_accruing ?? null,
      ratePct: sic.rate_pct ?? null,
      base: sic.base ?? null,
      baseLabel: sic.base_label ?? null,
      asOf: sic.as_of ?? null,
    };
  }
  const needs = sic?.needs ?? resp?.needs ?? [];
  // Backend: election leaves sic.start_basis null; only the top-level field carries it.
  if (resp?.interest_start_basis === 'election_needed') {
    return { kind: 'election', candidates: sic?.candidates ?? resp.candidates ?? [], needs };
  }
  return { kind: 'not_computed', needs, note: sic?.note ?? null };
}

export type AckTone = 'brand' | 'muted' | 'done';

export function ackBadge(status: string | null | undefined, basis: string | null | undefined): { label: string; tone: AckTone } {
  switch (status as S542AckStatus) {
    case 'BREACHED':
      return { label: 'Ack late (recorded)', tone: 'brand' };
    case 'PRESUMED_BREACHED':
      return { label: 'Ack presumed late (basis presumed)', tone: 'muted' };
    case 'TIMELY':
      return basis === 'recorded'
        ? { label: 'Ack timely (recorded)', tone: 'done' }
        : { label: 'Ack timely (basis presumed)', tone: 'muted' };
    default:
      return { label: 'Ack status pending', tone: 'muted' };
  }
}

export const TRIGGER_LABEL: Record<string, string> = {
  ack_late: 'Late acknowledgment (§542.055)',
  decision_late: 'Late accept/reject (§542.056(a))',
  payment_late: 'Late payment (§542.057/.058)',
};

export function triggerLabel(code: string | null | undefined): string {
  if (!code) return '—';
  return TRIGGER_LABEL[code] ?? code;
}
